<template>
  <div class="queue-layout">
    <div v-if="isInitLoading" class="project-tasks-layout">
      <TirPmSkeletonLoader width="18.75rem" height="2rem" border-radius="0.5rem" />
      <div class="project-tasks-layout__main-content-skeleton">
        <TirPmSkeletonLoader width="100%" height="75rem" border-radius="0.5rem" />
      </div>
    </div>
    <div class="queue-layout__header" v-if="isVisibleHeader && !isInitLoading">
      <RouterTabs class="queue-layout__tabs">
        <template #default="{ currentTab }">
          <RouterTab :route="{ name: 'kanban' }" label="Канбан" :currentTab="currentTab">
            <ViewColumnsIcon class="queue-layout__tab-icon kanban" />
          </RouterTab>
          <RouterTab :route="{ name: 'list' }" label="Список" :currentTab="currentTab">
            <QueueListIcon class="queue-layout__tab-icon list" />
          </RouterTab>
        </template>
      </RouterTabs>
      <ParentTaskPlate v-if="parentTask" :task="parentTask" />
    </div>
    <div v-if="!isInitLoading && (currentQueue?.Id || isInvited)" class="queue-layout__content">
      <RouterView
        @toggle-submenu="emit('toggle-submenu', $event)"
        @open-options="onOpenOptions"
        @checkIsUserAccessTask="emit('checkIsUserAccessTask', $event)"
        :namespaceName="namespaceName"
        :queueInfo="currentQueue"
        :isNoQueueInformation="isNoQueueInformation"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ComputedRef,
  inject,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  watch,
} from 'vue';
import { QueueListIcon, ViewColumnsIcon } from 'tir-style-system/icons/outline';
import { RouterTabs, RouterTab, ParentTaskModel, ParentTaskPlate } from '@/components';
import { useRoute, useRouter } from 'vue-router';
import { AdapterService } from './adapter';
import { ProjectQueueTaskListItem } from '@/components/tasks-kanban/adapter';
import { CurrentUser } from 'tir-pm-user-core';
import { TirPmSkeletonLoader } from 'tir-components';
import { ProjectQueueTaskGetRequestModel } from './models';

const router = useRouter();
const route = useRoute();

const adapter = new AdapterService();

/**Загрузка при инициализации */
const isInitLoading = ref(false);

const emit = defineEmits<{
  /**Проверка на доступ к задаче */
  (e: 'checkIsUserAccessTask', checkIsUserAccessTask: boolean);
  /**Событие для раскрытия сайтбара */
  (e: 'toggle-submenu', queueId: string): void;
  (e: 'open-options', queueId: string): void;
  /**При отсутсвии данных очереди */
  (e: 'isNoQueueInformation', isNoQueueInformation: boolean);
}>();

const props = defineProps({
  /**ID неймспейса */
  namespaceId: {
    type: String,
    required: true,
  },
  namespaceName: {
    type: String,
    required: true,
  },
});
/**Проверка приглашения */
const isInvited = inject<ComputedRef<boolean>>('isInvited');

/**Родительская задача */
const parentTask = ref<ParentTaskModel>();
/**Админ неймспейса */
const isOwner = ref(false);
const isAdminNamespace = ref(false);
const isAdminProject = ref(false);
const isCanAddTask = ref(false);
const currentQueue = ref(new ProjectQueueTaskListItem());
/**Отсутствие информации об очереди */
const isNoQueueInformation = ref(false);

const isVisibleHeader = computed(
  () =>
    router.currentRoute.value?.name &&
    ['kanban', 'list'].includes(router.currentRoute.value.name.toString()),
);

provide('isAdminNamespace', isAdminNamespace);
provide('currentQueue', currentQueue);
provide('isCanAddTask', isCanAddTask);
provide('isAdminProject', isAdminProject);
provide('isOwner', isOwner);

watch(
  () => props.namespaceId,
  async () => {
    isAdminNamespace.value = await adapter.checkNamespaceAdmin(props.namespaceId);
  },
);
watch(
  () => route.params.queueId,
  async () => {
    await updateQueue();
  },
);

onBeforeMount(async () => {
  isInitLoading.value = true;
  isAdminNamespace.value = await adapter.checkNamespaceAdmin(props.namespaceId);
  isInitLoading.value = false;
  await adapter.ToggleSubscribe(true);
  adapter.UpdateQueueTaskStatusEvent.on(updateQueue);
  await updateQueue();
});

/**Получение родительской задачи */
const getQueueParentTask = async (queueId: string) => {
  parentTask.value = undefined;
  const response = await adapter.getQueueParentTask(queueId);
  if (response.IsSuccess) {
    parentTask.value = response.Value;
  }
};
async function updateQueue() {
  const queueId = route.params.queueId?.toString();
  const taskId = route.params.taskId;
  if (queueId) {
    const res = await adapter.getQueueInfo(
      new ProjectQueueTaskGetRequestModel({ QueueTaskId: queueId }),
    );
    if (res.IsSuccess) {
      currentQueue.value = res.Value;
    } else {
      isNoQueueInformation.value = true;
      emit('isNoQueueInformation', true);
      if (!isInvited.value) {
        router.push({
          name: 'project-stub-page',
          query: {
            errorCode: res.ErrorCode,
          },
        });
      }
    }
    isCanAddTask.value = await IsCanAddTask();
    await getQueueParentTask(queueId);
  }
}

onBeforeUnmount(() => {
  adapter.ToggleSubscribe(false);
});

function onOpenOptions(queueId) {
  emit('open-options', queueId);
}

async function IsCanAddTask() {
  const user = CurrentUser;
  const isGood = user.Roles.includes(2); // owner
  isOwner.value = isGood;
  if (isGood) {
    isCanAddTask.value = true;
    return true;
  }
  const isProjectAdmin = await adapter.isProjectAdmin({
    ProjectId: route.params.projectId,
    UserId: user.Id,
  } as any);
  isAdminProject.value = Boolean(isProjectAdmin.Value);
  if (isProjectAdmin.Value) {
    isCanAddTask.value = true;
    return true;
  }

  if (isAdminNamespace.value) {
    isCanAddTask.value = true;
    return true;
  }

  const res = await adapter.getQueueUsersRoles({
    QueueTaskId: currentQueue?.value?.Id || route.params.queueId,
    UserId: user.Id,
  } as any);
  const xx = res.Value?.filter((el) => el?.Id == user?.Id);
  const resp = xx?.length > 0 && xx[0].Accesses.length > 0 && xx[0].Accesses[0]; //TODO: Добавить проверку на возможность добавления задачи
  isCanAddTask.value = resp;
  return resp;
}
</script>

<style lang="scss" scoped>
@keyframes skeleton-loading {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.project-tasks-layout {
  &__menu-breadcrumbs-skeleton {
    width: 18.75rem;
    height: 2rem;
    border-radius: 0.5rem;
    background-color: var(--neutral-10, #eaeaeb);
  }
  &__main-skeleton {
    height: 75rem;
    border-radius: 0.5rem 0.5rem 0rem 0rem;
    background-color: var(--neutral-10, #eaeaeb);
  }
  &__main-content-skeleton {
    margin-top: 1.313rem;
    width: 100%;
    gap: 0.75rem;
  }

  &__menu-breadcrumbs-skeleton,
  &__main-skeleton {
    position: relative;
    overflow: hidden;
    background-color: var(--neutral-10, #eaeaeb);
  }

  &__main-skeleton::before,
  &__menu-breadcrumbs-skeleton::before,
  &__sidebar-menu-skeleton::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: skeleton-loading 1.5s infinite;
  }
}
.queue-layout {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 0.625rem;
    margin-bottom: 1rem;
    min-height: 2.25rem;
  }

  &__tabs {
    width: fit-content;
  }
  &__tab-icon {
    width: 1.5rem;
    height: 1.5rem;
    padding: 0.25rem;
    color: var(--status-info);
  }

  &__content {
    margin-top: 1rem;
  }
}
</style>
